/**
 * FunnelTrackingService
 *
 * Manages funnel progression per supplier per session.
 * Automatically calculates stage durations, drop-off points,
 * and conversion metrics.
 *
 * Funnel stages (ordered):
 *   Landing → Search → Results → CartAdd → CartView →
 *   Checkout → ProceedToBuy → [CreatePO → GeneratePO] → Complete
 *
 * The CreatePO/GeneratePO stages apply only to Local Supplier.
 */
import { Injectable } from '@angular/core';
import {
    FunnelSnapshot,
    FunnelStage,
    SupplierType,
} from '../models/analytics.interfaces';
import { AnalyticsStorageService } from './analytics-storage.service';

@Injectable()
export class FunnelTrackingService {
    /** In-memory funnel state per supplier (sessionId:supplier → snapshot) */
    private activeFunnels = new Map<string, FunnelSnapshot>();
    private sessionId = '';

    constructor(private storage: AnalyticsStorageService) { }

    // ───────────── Session ──────────────

    setSessionId(sessionId: string): void {
        this.sessionId = sessionId;
    }

    // ───────────── Progression ──────────────

    /**
     * Record that a user reached a funnel stage for a given supplier.
     * Stages must be reached in order; skipping a stage is allowed
     * (the skipped stages are marked as bypassed).
     */
    reachStage(supplier: SupplierType | string, stage: FunnelStage): void {
        const key = this.funnelKey(supplier);
        let funnel = this.activeFunnels.get(key);

        if (!funnel) {
            funnel = this.createFunnel(supplier);
            this.activeFunnels.set(key, funnel);
        }

        // Don't re-record a stage already reached
        if (funnel.stagesReached.includes(stage)) return;

        const now = new Date().toISOString();
        funnel.stagesReached.push(stage);
        funnel.stageTimestamps[stage] = now;

        // Calculate duration from previous stage
        const prevStage = funnel.stagesReached[funnel.stagesReached.length - 2];
        if (prevStage && funnel.stageTimestamps[prevStage]) {
            const prevTime = new Date(funnel.stageTimestamps[prevStage]).getTime();
            const nowTime = new Date(now).getTime();
            funnel.stageDurations[`${prevStage}_to_${stage}`] = nowTime - prevTime;
        }

        // Calculate total funnel time
        const firstTimestamp = funnel.stageTimestamps[funnel.stagesReached[0]];
        if (firstTimestamp) {
            funnel.totalFunnelTimeMs =
                new Date(now).getTime() - new Date(firstTimestamp).getTime();
        }

        // Check if funnel is complete
        if (stage === FunnelStage.Complete) {
            funnel.completed = true;
            funnel.dropOffStage = null;
        }

        // Persist to storage
        this.storage.saveFunnelSnapshot(key, funnel);
    }

    // ───────────── Drop-off ──────────────

    /**
     * Mark a funnel as dropped off at a specific stage.
     * Called when user navigates away, session times out, etc.
     */
    markDropOff(supplier: SupplierType | string, reason?: string): void {
        const key = this.funnelKey(supplier);
        const funnel = this.activeFunnels.get(key);
        if (!funnel || funnel.completed) return;

        const lastStage =
            funnel.stagesReached[funnel.stagesReached.length - 1] || FunnelStage.Landing;
        funnel.dropOffStage = lastStage;

        // Recalculate total time
        const firstTimestamp = funnel.stageTimestamps[funnel.stagesReached[0]];
        if (firstTimestamp) {
            funnel.totalFunnelTimeMs =
                Date.now() - new Date(firstTimestamp).getTime();
        }

        this.storage.saveFunnelSnapshot(key, funnel);
    }

    /**
     * Mark all active (non-completed) funnels as dropped off.
     * Called on session end.
     */
    markAllDropOff(): void {
        this.activeFunnels.forEach((funnel, key) => {
            if (!funnel.completed && funnel.dropOffStage === null) {
                const lastStage =
                    funnel.stagesReached[funnel.stagesReached.length - 1] ||
                    FunnelStage.Landing;
                funnel.dropOffStage = lastStage;
                const firstTimestamp =
                    funnel.stageTimestamps[funnel.stagesReached[0]];
                if (firstTimestamp) {
                    funnel.totalFunnelTimeMs =
                        Date.now() - new Date(firstTimestamp).getTime();
                }
                this.storage.saveFunnelSnapshot(key, funnel);
            }
        });
    }

    // ───────────── Update Final Values ──────────────

    updateFinalValues(
        supplier: SupplierType | string,
        value: number,
        itemCount: number
    ): void {
        const key = this.funnelKey(supplier);
        const funnel = this.activeFunnels.get(key);
        if (!funnel) return;

        funnel.finalValue = value;
        funnel.finalItemCount = itemCount;
        this.storage.saveFunnelSnapshot(key, funnel);
    }

    // ───────────── Query ──────────────

    getFunnel(supplier: SupplierType | string): FunnelSnapshot | undefined {
        return this.activeFunnels.get(this.funnelKey(supplier));
    }

    getCurrentStage(supplier: SupplierType | string): FunnelStage | null {
        const funnel = this.activeFunnels.get(this.funnelKey(supplier));
        if (!funnel || funnel.stagesReached.length === 0) return null;
        return funnel.stagesReached[funnel.stagesReached.length - 1];
    }

    /**
     * Get conversion rate between two stages across all funnels in storage.
     */
    getConversionRate(
        fromStage: FunnelStage,
        toStage: FunnelStage
    ): { rate: number; reached: number; total: number } {
        const allFunnels = Object.values(this.storage.getAllFunnelSnapshots());
        const reachedFrom = allFunnels.filter((f) =>
            f.stagesReached.includes(fromStage)
        ).length;
        const reachedTo = allFunnels.filter((f) =>
            f.stagesReached.includes(toStage)
        ).length;

        return {
            rate: reachedFrom > 0 ? reachedTo / reachedFrom : 0,
            reached: reachedTo,
            total: reachedFrom,
        };
    }

    /**
     * Get aggregated drop-off distribution.
     */
    getDropOffDistribution(): Record<string, number> {
        const allFunnels = Object.values(this.storage.getAllFunnelSnapshots());
        const distribution: Record<string, number> = {};

        for (const funnel of allFunnels) {
            if (funnel.dropOffStage) {
                distribution[funnel.dropOffStage] =
                    (distribution[funnel.dropOffStage] || 0) + 1;
            }
        }
        return distribution;
    }

    // ───────────── Reset ──────────────

    resetForSession(): void {
        this.activeFunnels.clear();
    }

    // ───────────── Internals ──────────────

    private funnelKey(supplier: SupplierType | string): string {
        return `${this.sessionId}:${supplier}`;
    }

    private createFunnel(supplier: SupplierType | string): FunnelSnapshot {
        return {
            sessionId: this.sessionId,
            supplier: supplier as string,
            stagesReached: [],
            stageTimestamps: {},
            stageDurations: {},
            dropOffStage: null,
            totalFunnelTimeMs: 0,
            completed: false,
            finalValue: 0,
            finalItemCount: 0,
        };
    }
}
